import { type Inspection } from '@lib/spproject/types';
import {
  ruleTargets, type FormInputType, type LocalizedText, type PoiField, type PoiFieldCondition, type PoiFieldGroup,
  type PoiFieldGroupRule, type PoiFieldRule, type PoiTypeBody, type ResolvedItem, type RuleOperator,
} from '@store/api/poiTypeEndpoints';

/**
 * Evaluating a POI type's conditional rules.
 *
 * The device is the real consumer — Composer embeds a resolved schema into a checklist and
 * answers these rules against an inspector's answers — so this module exists for two
 * narrower jobs: driving the authoring preview, so a rule can be tried before it ships,
 * and telling the editor which operators a field can be asked about.
 *
 * `backend/doc/poi-field-rules.md` is the contract both implementations follow. Anything
 * changed here has to change there, and in Composer.
 */

export type PoiRuleValue = string | number | boolean | string[] | undefined;

/**
 * The one thing a condition can ask about that is not a field: the status the inspector gave
 * the step the form sits on. It is named where a field would be, by an id no field may carry
 * (the API refuses a field id that begins with `$`), so a condition keeps its one shape.
 * Mirrors the backend's constants of the same names in `poi-type.model.ts`; §13 of the
 * contract says why it is an id and not a key of its own.
 */
export const STEP_STATUS_ID = '$stepStatus';

/** What a step's status can be, by the names the device writes and a condition compares. */
export const stepStatuses = ['NotChecked', 'Ok', 'Warning', 'Error'] as const satisfies readonly Inspection.NoticeType[];

/** What a condition points at: a field, by its input type, or the step's status. */
export type RuleSubjectType = FormInputType | 'StepStatus';

/** What a condition can point at: its type decides the comparison, its value answers it. */
export interface RuleSubject {
  type: RuleSubjectType;
  value: PoiRuleValue;
}

/**
 * The answers a rule is evaluated against, by field id, and the step's status among them
 * under its reserved id.
 *
 * A `Map` rather than a `Record` because the distinction it draws is the whole contract: a
 * field present with an `undefined` value exists but is unanswered, and follows ordinary
 * semantics; a field absent from the map does not exist at all, and every condition against
 * it — including the negative ones — is false.
 */
export interface RuleContext {
  subjects: ReadonlyMap<string, RuleSubject>;
}

export interface EffectiveFlags {
  visible: boolean;
  required: boolean;
  readOnly: boolean;
}

export type RuleValueKind = 'none' | 'single' | 'text' | 'list' | 'number' | 'count';

const ALL_TYPES: readonly FormInputType[] = ['Text', 'Number', 'Barcode', 'List', 'MultiList', 'Toggle', 'Range'];

/**
 * The types a device always records an answer for, so neither emptiness nor `required` means
 * anything on them. A Toggle holds `false`, which is an answer. A Range with no default
 * starts its slider at `min`, and `min` is recorded if the inspector never moves it — the
 * stored default stays empty all the same, since that is Composer's rule, not the author's.
 */
const ALWAYS_ANSWERED: readonly FormInputType[] = ['Toggle', 'Range'];

export const isAlwaysAnswered = (type: FormInputType): boolean => ALWAYS_ANSWERED.includes(type);

const CAN_BE_EMPTY = ALL_TYPES.filter((type) => !isAlwaysAnswered(type));

/**
 * Which subjects each operator may be asked about — the type of the field a condition
 * *points at*, not of the field carrying the rule. Mirrors the backend's table of the same
 * name in `poi-type.model.ts`, which is what rejects an impossible rule on the way in.
 */
export const OPERATOR_FIELD_TYPES: Record<RuleOperator, readonly RuleSubjectType[]> = {
  equals: ALL_TYPES,
  notEquals: ALL_TYPES,
  contains: ['Text', 'Barcode'],
  notContains: ['Text', 'Barcode'],
  // The step's status is asked this way and no other: it is one of four names, never empty
  // and never a number.
  isAnyOf: ['List', 'MultiList', 'StepStatus'],
  isNoneOf: ['List', 'MultiList', 'StepStatus'],
  gt: ['Number', 'Range'],
  gte: ['Number', 'Range'],
  lt: ['Number', 'Range'],
  lte: ['Number', 'Range'],
  countEquals: ['MultiList'],
  countGt: ['MultiList'],
  countLt: ['MultiList'],
  isEmpty: CAN_BE_EMPTY,
  isNotEmpty: CAN_BE_EMPTY,
};

/** What the condition's `value` has to be, which is what the editor renders an input for. */
export const OPERATOR_VALUE_KIND: Record<RuleOperator, RuleValueKind> = {
  equals: 'single', notEquals: 'single',
  contains: 'text', notContains: 'text',
  isAnyOf: 'list', isNoneOf: 'list',
  gt: 'number', gte: 'number', lt: 'number', lte: 'number',
  countEquals: 'count', countGt: 'count', countLt: 'count',
  isEmpty: 'none', isNotEmpty: 'none',
};

const OPERATORS = Object.keys(OPERATOR_VALUE_KIND) as RuleOperator[];

/** The operators a subject of this type can be asked about, in the order the editor lists them. */
export const operatorsFor = (type: RuleSubjectType): RuleOperator[] =>
  OPERATORS.filter((operator) => OPERATOR_FIELD_TYPES[operator].includes(type));

/**
 * The answer in the shape its type promises, so every operator below compares like with
 * like. An unanswered field normalizes to its type's empty answer rather than staying
 * `undefined` — a `Text` nobody filled in genuinely does not contain "crack".
 */
const normalize = (subject: RuleSubject): PoiRuleValue => {
  switch (subject.type) {
    case 'Toggle':
      return Boolean(subject.value);
    case 'MultiList':
      return Array.isArray(subject.value) ? subject.value : [];
    case 'Number':
    case 'Range':
      return typeof subject.value === 'number' && Number.isFinite(subject.value) ? subject.value : undefined;
    default:
      return typeof subject.value === 'string' ? subject.value : '';
  }
};

const sameSet = (left: unknown, right: unknown): boolean => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;

  const a = new Set(left);
  const b = new Set(right);

  return a.size === b.size && [...a].every((entry) => b.has(entry));
};

/**
 * Whether the answer shares an option id with `set` — the answer being one id on a List,
 * several on a MultiList. An unanswered List is `''` after `normalize`, which no set holds.
 */
const intersects = (value: PoiRuleValue, set: unknown): boolean => {
  if (!Array.isArray(set)) return false;

  const selected = Array.isArray(value) ? value : [value];

  return selected.some((id) => set.includes(id as string));
};

const isEmptyAnswer = (type: RuleSubjectType, value: PoiRuleValue): boolean => {
  if (type === 'MultiList') return !Array.isArray(value) || value.length === 0;
  if (type === 'Number') return typeof value !== 'number';

  return value === undefined || value === '';
};

/** Selections on a multi-select. An unanswered one has none, which is a count, not a gap. */
const countOf = (value: PoiRuleValue): number => (Array.isArray(value) ? value.length : 0);

const compare = (operator: RuleOperator, left: PoiRuleValue, right: unknown): boolean => {
  if (typeof left !== 'number' || typeof right !== 'number') return false;

  switch (operator) {
    case 'gt': return left > right;
    case 'gte': return left >= right;
    case 'lt': return left < right;
    default: return left <= right;
  }
};

/**
 * Whether one condition holds.
 *
 * Total: anything it cannot evaluate is `false`, and that includes the negative operators.
 * `notEquals` against a field that no longer exists is false, not true — a rule whose
 * subject has gone is inert, never inverted. Only the *gate* short-circuits that way; past
 * it a negative operator is the exact complement of its positive, so an unanswered `Text`
 * does satisfy `notContains`.
 */
export const conditionMatches = (condition: PoiFieldCondition, context: RuleContext): boolean => {
  const subject = context.subjects.get(condition.fieldId);

  // The reference is dangling, or an ancestor retyped the field out from under the rule.
  if (!subject || !OPERATOR_FIELD_TYPES[condition.operator]?.includes(subject.type)) return false;

  const value = normalize(subject);
  const expected = condition.value;

  switch (condition.operator) {
    case 'isEmpty':
      return isEmptyAnswer(subject.type, value);
    case 'isNotEmpty':
      return !isEmptyAnswer(subject.type, value);

    case 'equals':
      return subject.type === 'MultiList' ? sameSet(value, expected) : value === expected;
    case 'notEquals':
      return !(subject.type === 'MultiList' ? sameSet(value, expected) : value === expected);

    // Ordinal and case-sensitive, deliberately: a case-insensitive test needs a culture,
    // and this installation ships Turkish, where "I" lowercases differently than it does
    // under the invariant culture. Two devices would disagree. Not a regular expression
    // either — an author who wants anchoring has the field's own `pattern`.
    case 'contains':
      return typeof value === 'string' && typeof expected === 'string' && value.includes(expected);
    case 'notContains':
      return !(typeof value === 'string' && typeof expected === 'string' && value.includes(expected));

    // On a List the one selected id is in the set; on a MultiList at least one selected id
    // is — intersection, never "every selection is from the set". A one-id set is how a
    // single option is asked about.
    case 'isAnyOf':
      return intersects(value, expected);
    case 'isNoneOf':
      return !intersects(value, expected);

    case 'countEquals':
      return countOf(value) === expected;
    case 'countGt':
      return typeof expected === 'number' && countOf(value) > expected;
    case 'countLt':
      return typeof expected === 'number' && countOf(value) < expected;

    default:
      return compare(condition.operator, value, expected);
  }
};

/** Whether a rule fires: `all` needs every condition, `any` needs one. Never empty. */
export const ruleMatches = (
  rule: Pick<PoiFieldRule, 'when'>, context: RuleContext
): boolean => {
  const { match, conditions } = rule.when;

  if (!conditions?.length) return false;

  return match === 'any'
    ? conditions.some((condition) => conditionMatches(condition, context))
    : conditions.every((condition) => conditionMatches(condition, context));
};

/**
 * A group's effective visibility: its own flag, then each matching rule's `then` over it.
 * `visible` is all a group's rule can set — the other flags would be a cascade onto its
 * fields rather than an overwrite, and each one would cascade differently.
 */
export const evaluateGroup = (
  group: Pick<PoiFieldGroup, 'visible' | 'rules'>, context: RuleContext
): { visible: boolean } => {
  let visible = group.visible !== false;

  for (const rule of group.rules ?? []) {
    if (ruleMatches(rule, context)) visible = rule.then.visible;
  }

  return { visible };
};

/**
 * A field's effective flags.
 *
 * Start from what the field stores, walk its rules in array order, and let each matching
 * rule's `then` overwrite what came before — **the last matching rule wins, per property**.
 * There is no priority and no first-match-wins. Conditions read other fields' raw answers
 * and never their computed flags, so this is one pass with nothing to converge and a cycle
 * between two fields is well defined rather than forbidden.
 *
 * `group` ANDs its own visibility in, so no caller can render a field inside a hidden
 * section by forgetting the cascade.
 */
export const evaluateField = (
  field: PoiField, context: RuleContext, group?: { visible: boolean }
): EffectiveFlags => {
  const flags: EffectiveFlags = {
    visible: field.visible !== false,
    required: field.required,
    readOnly: field.readOnly,
  };

  for (const rule of field.rules ?? []) {
    if (!ruleMatches(rule, context)) continue;

    // Only what a rule may set: a snapshot an older app wrote can carry a key the contract no
    // longer has, and it decides nothing.
    for (const target of ruleTargets) {
      const value = rule.then[target];

      if (typeof value === 'boolean') flags[target] = value;
    }
  }

  if (group && !group.visible) flags.visible = false;

  // A question nobody was asked cannot be mandatory, nor is it printed on a report (§7).
  if (!flags.visible) flags.required = false;

  return flags;
};

/**
 * Every field of a resolved schema, in render order, paired with the answer it holds, and the
 * status of the step the form sits on: that step's own, never the worst of its point.
 */
export const ruleContext = (
  items: ResolvedItem[], values: Record<string, PoiRuleValue>, stepStatus: Inspection.NoticeType
): RuleContext => {
  const subjects = new Map<string, RuleSubject>();

  for (const entry of items) {
    const fields = entry.kind === 'field' ? [entry.field] : entry.group.fields;

    for (const field of fields) {
      // A field with no id has never been saved, so nothing can reference it yet. Walking
      // in order means a type's own field shadows an ancestor's sharing its id — the same
      // "nearest wins" the server validates against.
      if (field.id) subjects.set(field.id, { type: field.type, value: values[field.id] });
    }
  }

  // Last, so nothing a snapshot written by hand declares under the reserved id stands in for it.
  subjects.set(STEP_STATUS_ID, { type: 'StepStatus', value: stepStatus });

  return { subjects };
};

/** Every field a type declares, ungrouped ones first, then each group's — the write order. */
const allFieldsOf = (body: Pick<PoiTypeBody, 'fields' | 'groups'>): PoiField[] =>
  [...body.fields, ...body.groups.flatMap((group) => group.fields)];

/** Everything that can carry rules: the fields, and the groups themselves. */
const carriersOf = (body: Pick<PoiTypeBody, 'fields' | 'groups'>) =>
  [...allFieldsOf(body), ...body.groups];

/**
 * The conditions pointing at one field, so a deletion can say what it is about to take
 * with it rather than letting `pruneRules` remove it quietly.
 */
export const rulesReferencing = (
  fieldId: string | undefined, body: Pick<PoiTypeBody, 'fields' | 'groups'>
): PoiFieldCondition[] => {
  if (!fieldId) return [];

  return carriersOf(body).flatMap((carrier) =>
    (carrier.rules ?? []).flatMap((rule: PoiFieldRule | PoiFieldGroupRule) =>
      rule.when.conditions.filter((condition) => condition.fieldId === fieldId)));
};

/** Whether a condition has everything the API requires of it: a field, and a value where one is due. */
export const conditionComplete = (condition: PoiFieldCondition): boolean => {
  const kind = OPERATOR_VALUE_KIND[condition.operator];

  if (!condition.fieldId) return false;
  if (kind === 'none') return true;
  if (kind === 'list') return Array.isArray(condition.value) && condition.value.length > 0;

  return condition.value !== undefined && condition.value !== '';
};

export type ConditionProblem = 'missing' | 'operator' | 'option';

/** What a condition is held against: a field's type and choices, or the status and its names. */
interface ConditionTarget {
  type: RuleSubjectType;
  options?: { id?: string }[];
}

/** The step's status as a target: its four names stand where a field's choices would. */
const STEP_STATUS_TARGET: ConditionTarget = { type: 'StepStatus', options: stepStatuses.map((id) => ({ id })) };

/**
 * Why a condition no longer fits the field it points at, or null when it does: the field is
 * gone, its type cannot answer the operator, or a choice it names is not declared.
 *
 * `pruneRules` drops a condition for any of these; the designer refuses to save one instead,
 * so a condition typed with a wrong id is not dropped without a word.
 *
 * A condition on the step's status is held against the status itself whatever `target` is:
 * it names no field, so a caller looking it up among the fields finds nothing, and taken for
 * a missing field it would be pruned from every save.
 */
export const conditionProblem = (
  condition: PoiFieldCondition, found: ConditionTarget | undefined
): ConditionProblem | null => {
  const target = condition.fieldId === STEP_STATUS_ID ? STEP_STATUS_TARGET : found;

  if (!target) return 'missing';
  if (!OPERATOR_FIELD_TYPES[condition.operator]?.includes(target.type)) return 'operator';

  const kind = OPERATOR_VALUE_KIND[condition.operator];
  const declared = new Set((target.options ?? []).map((option) => option.id));
  const isDeclared = (id: unknown) => declared.has(id as string);
  const allDeclared = Array.isArray(condition.value) && condition.value.every(isDeclared);

  if (kind === 'list') return allDeclared ? null : 'option';

  // `equals` against a choice field also names option ids; every other shape is untouched
  // by what happened to the target, so it survives.
  if (kind === 'single' && target.type === 'List') return isDeclared(condition.value) ? null : 'option';
  if (kind === 'single' && target.type === 'MultiList') return allDeclared ? null : 'option';

  return null;
};

const conditionStillHolds = (condition: PoiFieldCondition, known: ReadonlyMap<string, PoiField>): boolean =>
  !conditionProblem(condition, known.get(condition.fieldId));

/**
 * Drops the conditions an outgoing write has just invalidated, and any rule left with none.
 *
 * `PUT` replaces the whole type, so every save re-sends every rule — which means deleting a
 * field, retyping one, or removing an option leaves conditions pointing at something that
 * no longer fits *within the same document*, and the server rejects the next save, however
 * unrelated that save is. One pass over the body on the way out is the single place that
 * can see all three cases at once.
 *
 * Only what the write itself breaks: an ancestor's fields are passed in so a reference that
 * reaches up the inheritance chain is not mistaken for a dangling one.
 */
export const pruneRules = <T extends Pick<PoiTypeBody, 'fields' | 'groups'>>(
  body: T, ancestorFields: ReadonlyMap<string, PoiField>
): T => {
  const known = new Map(ancestorFields);

  allFieldsOf(body).forEach((field) => {
    if (field.id) known.set(field.id, field);
  });

  const prune = <R extends PoiFieldRule | PoiFieldGroupRule>(rules: R[] | undefined): R[] | undefined => {
    if (!rules?.length) return rules;

    const kept = rules
      .map((rule) => ({
        ...rule,
        when: {
          ...rule.when,
          conditions: rule.when.conditions.filter((condition) => conditionStillHolds(condition, known)),
        },
      }))
      .filter((rule) => rule.when.conditions.length > 0);

    return kept.length ? kept : undefined;
  };

  return {
    ...body,
    fields: body.fields.map((field) => ({ ...field, rules: prune(field.rules) })),
    groups: body.groups.map((group) => ({
      ...group,
      rules: prune(group.rules),
      fields: group.fields.map((field) => ({ ...field, rules: prune(field.rules) })),
    })),
  };
};

/**
 * The POI type Composer copies into an inspection step, with the inspector's answers
 * written into each field's `value` — §12 of `backend/doc/poi-field-rules.md`.
 *
 * Not `/effective`'s shape: an entry is `{ field, group }` with the unused side `null`, and
 * a field carries every input type's keys, the ones its type does not use set to `null`.
 * Typed as `PoiField` anyway, because nothing here reads a key a field's type does not use.
 */
export interface CustomFieldsSnapshot {
  id: string;
  name: LocalizedText;
  /** The type's `version` when it was copied. Never a reason to replace the copy. */
  version: number;
  items: { field: PoiField | null; group: PoiFieldGroup | null }[];
}

/**
 * A step's `customFields`, if it is shaped like a snapshot at all.
 *
 * Insight stores the snapshot as uploaded and validates none of it, so a reader cannot take
 * its shape on trust: this is the one place that narrows it, and anything that fails
 * narrowing is treated as no snapshot rather than as an error.
 */
export const asSnapshot = (value: unknown): CustomFieldsSnapshot | null => {
  if (!value || typeof value !== 'object') return null;

  return Array.isArray((value as { items?: unknown }).items) ? value as CustomFieldsSnapshot : null;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

/**
 * The snapshot's entries in the resolved shape the evaluator reads. Inheritance is already
 * flattened into a snapshot, so every entry is the type's own; an entry that is neither a
 * field nor a group is dropped rather than allowed to break the rest.
 */
export const snapshotItems = (snapshot: CustomFieldsSnapshot): ResolvedItem[] =>
  snapshot.items.flatMap((entry): ResolvedItem[] => {
    if (!isObject(entry)) return [];
    if (isObject(entry.field)) return [{ kind: 'field', field: entry.field, inheritedFrom: null }];

    if (isObject(entry.group)) {
      const fields = Array.isArray(entry.group.fields) ? entry.group.fields.filter(isObject) : [];

      return [{ kind: 'group', group: { ...entry.group, fields }, inheritedFrom: null }];
    }

    return [];
  });

/** A run of answers: a group with its visible fields, or consecutive ungrouped fields. */
export interface AnswerSection {
  group: PoiFieldGroup | null;
  fields: PoiField[];
}

/**
 * What the inspector was actually shown: the snapshot's rules evaluated against its own
 * answers and the status of the step it sits on, and every field they hid — or that sits in
 * a group they hid — left out.
 *
 * A hidden field's answer is still in the snapshot (§7 keeps it), but a question nobody was
 * asked is not an answer worth showing. Sections keep the snapshot's order, and a group with
 * nothing left visible in it is dropped with them.
 */
export const visibleAnswers = (
  snapshot: CustomFieldsSnapshot, stepStatus: Inspection.NoticeType
): AnswerSection[] => {
  const items = snapshotItems(snapshot);
  const values: Record<string, PoiRuleValue> = {};

  for (const entry of items) {
    for (const field of entry.kind === 'field' ? [entry.field] : entry.group.fields) {
      if (field.id) values[field.id] = (field.value ?? undefined) as PoiRuleValue;
    }
  }

  const context = ruleContext(items, values, stepStatus);
  const sections: AnswerSection[] = [];

  for (const entry of items) {
    if (entry.kind === 'field') {
      if (!evaluateField(entry.field, context).visible) continue;

      const last = sections[sections.length - 1];

      if (last && last.group === null) last.fields.push(entry.field);
      else sections.push({ group: null, fields: [entry.field] });

      continue;
    }

    const group = evaluateGroup(entry.group, context);

    if (!group.visible) continue;

    const fields = entry.group.fields.filter((field) => evaluateField(field, context, group).visible);

    if (fields.length) sections.push({ group: entry.group, fields });
  }

  return sections;
};
